import React from "react";
import { CircleAlert, CircleCheckBig, Info, Star, AlertCircle, ChevronDown } from "lucide-react";
import ContentsContainer from "./ContentsContainer";

const sections = [
  {
    sectionId: "welcome",
    sectionTitle: "Welcome to Hyvid",
    searchKeywords: `
      hyvid hyvidone employee handbook  guide rulebook
      purpose curiosity care culture values mission vision
      expectations day-to-day benefits health safety rights
      local law compliance policies procedures support evolve
      global team remote distributed international worldwide
      consistency fairness fair clear updates latest version
      documentation company organization workplace employment employee
      onboarding orientation welcome introduction getting started
      company values company culture working with hyvid future 
      `,
    sectionBody: (
      <>
        <div className="flex gap-3 p-3 rounded-lg border bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700">
          <CircleCheckBig className="w-4 h-4 text-emerald-500 dark:text-emerald-500 flex-shrink-0 mt-0.5" />

          <div className="text-sm">
            <p className="font-semibold text-emerald-800 dark:text-emerald-100">
              We're so glad you're here.
            </p>
          </div>
        </div>

        <p>
          At Hyvid, we believe great work isn't defined by where you are,
          but by how you show up — with{" "}
          <strong>purpose, curiosity, and care.</strong>
        </p>

        <p>
          This handbook is here to help you understand how we work, what
          we stand for, and how we support you as part of our global
          team. You'll find everything you need to know about working
          with Hyvid: from day-to-day expectations and benefits, to
          health, safety, and your rights under local law.
        </p>

        <p>
          Think of it as a guide — not just a rulebook. It's designed to
          make things clear, fair, and consistent for everyone,
          wherever you work in the world.
        </p>

        <p>
          We'll keep this handbook updated as we evolve. The latest
          version will always be available in{" "}
          <strong>HyvidOne</strong>.
        </p>

        <p className="text-emerald-700 dark:text-emerald-200 dark:text-emerald-200 font-medium">
          Welcome to the team. We're thrilled to have you shaping the
          future of global work with us.
        </p>
      </>
    ),
  },

  {
    sectionId: "who-we-are",
    sectionTitle: "Who We Are",
    searchKeywords: `
      hyvid purpose vision mission unique proposition values company culture
      global workforce talent smartest teams ai artificial intelligence
      ai-powered ai integration high-performance people businesses clients employees
      innovation excellence integrity opportunity connection transparency accountability respect
      inclusion collaboration teamwork leadership relationships decision making growth success
      impact support productivity efficiency solutions technology future digital transformation
      performance worldwide international remote distributed
    `,
    sectionBody: (
      <>
        <div className="grid sm:grid-cols-2 gap-3">
          
          <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-purple-50 dark:from-emerald-900 dark:to-purple-900 dark:from-emerald-900 dark:to-purple-900 border border-emerald-100 dark:border-emerald-800 dark:border-emerald-800">
            <div className="text-xs font-bold text-emerald-700 dark:text-emerald-200 mb-1">
              Our Purpose
            </div>

            <div className="text-sm muted-text">
              Helping the world work better, together.
            </div>
          </div>

          <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-purple-50 dark:from-emerald-900 dark:to-purple-900 border border-emerald-100 dark:border-emerald-800">
            <div className="text-xs font-bold text-emerald-700 dark:text-emerald-200 mb-1">
              Our Vision
            </div>

            <div className="text-sm muted-text">
              A smarter global workforce. Powered by people,
              integrated with AI.
            </div>
          </div>

          <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-purple-50 dark:from-emerald-900 dark:to-purple-900 border border-emerald-100 dark:border-emerald-800">
            <div className="text-xs font-bold text-emerald-700 dark:text-emerald-200 mb-1">
              Our Mission
            </div>

            <div className="text-sm muted-text">
              Building the world's smartest teams by integrating
              global talent and AI into one high-performance
              workforce.
            </div>
          </div>

          <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-purple-50 dark:from-emerald-900 dark:to-purple-900 border border-emerald-100 dark:border-emerald-800">
            <div className="text-xs font-bold text-emerald-700 dark:text-emerald-200 mb-1">
              Our Unique Proposition
            </div>

            <div className="text-sm muted-text">
              Your smartest team yet. Where global talent and AI
              converge.
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />

          <div className="text-sm">
            <span>
              Leading businesses into the future, with AI-powered
              teams that help them work smarter and grow faster.
            </span>
          </div>
        </div>

        <div className="mb-4">
          
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>

            Our Values
          </h3>

          <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
            
            <p className="mb-2">
              At Hyvid, our values guide every action,
              decision, and relationship.
            </p>

            <div className="space-y-2">

              <div className="flex items-start gap-3 p-2.5 rounded-lg dark-mode-card border border-gray-100">
                <div className="w-2 h-full min-h-6 rounded-full bg-gradient-to-b from-yellow-400 to-orange-400 flex-shrink-0"></div>

                <div>
                  <span className="font-semibold title-text">
                    Excellence
                  </span>

                  <span className="muted-text">
                    {" "}– We never settle. We bring rigor,
                    energy, and ambition to everything we do.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-2.5 rounded-lg dark-mode-card border border-gray-100">
                <div className="w-2 h-full min-h-6 rounded-full bg-gradient-to-b from-blue-400 to-cyan-400 flex-shrink-0"></div>

                <div>
                  <span className="font-semibold title-text">
                    Innovation
                  </span>

                  <span className="muted-text">
                    {" "}– We stay at the forefront of technology
                    to create smarter, faster, more intuitive
                    solutions.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-2.5 rounded-lg dark-mode-card border border-gray-100">
                <div className="w-2 h-full min-h-6 rounded-full bg-gradient-to-b from-emerald-400 to-green-400 flex-shrink-0"></div>

                <div>
                  <span className="font-semibold title-text">
                    Integrity
                  </span>

                  <span className="muted-text">
                    {" "}– We lead with transparency,
                    accountability, and respect. Always.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-2.5 rounded-lg dark-mode-card border border-gray-100">
                <div className="w-2 h-full min-h-6 rounded-full bg-gradient-to-b from-purple-400 to-pink-400 flex-shrink-0"></div>

                <div>
                  <span className="font-semibold title-text">
                    Opportunity
                  </span>

                  <span className="muted-text">
                    {" "}– We unlock pathways for clients and
                    employees to grow, succeed, and make an impact.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-2.5 rounded-lg dark-mode-card border border-gray-100">
                <div className="w-2 h-full min-h-6 rounded-full bg-gradient-to-b from-red-400 to-pink-400 flex-shrink-0"></div>

                <div>
                  <span className="font-semibold title-text">
                    Connection
                  </span>

                  <span className="muted-text">
                    {" "}– We make sure everyone feels supported
                    and included, even across distance.
                  </span>
                </div>
              </div>

            </div>

            <p className="mt-2 muted-text">
              These are more than words. They guide how we
              collaborate, make decisions, and show up for each
              other and our clients — every day.
            </p>
          </div>
        </div>
      </>
    ),
  },

  {
  sectionId: "general",
  sectionTitle: "General Provisions",
  searchKeywords: `
    handbook hyvid hyvidone policies policy source of truth current version
    notifications updates team members roles locations compliance commitment
    applies applicability policy changes revised updated published interpretation
    interpreting people culture human resources hr guidance clarification support
  `,
  sectionBody: (
    <>
      <p>
        This handbook is our single source of truth for all
        Hyvid policies. You can find the current version in
        HyvidOne at any time. You'll also receive updates or reminders through
        HyvidOne notifications if something important changes.
      </p>

      <div className="mb-4">

        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>

          Who It Applies To
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            All Hyvid team members — regardless of role,
            team, or location. Everyone is expected to understand and follow
            these policies as part of their commitment to
            working at Hyvid.
          </p>
        </div>
      </div>

      <div className="mb-4">

        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>

          If Policies Change
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Sometimes we'll need to adjust how things work.
            When that happens, you'll receive a HyvidOne
            notification that an updated version has been
            published.
          </p>
        </div>
      </div>

      <div className="mb-4">

        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>

          Interpreting the Handbook
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            If you're unsure how something applies to your
            situation, check in with{" "}
            <strong>People & Culture</strong>.
          </p>
        </div>
      </div>
    </>
  ),
},
{
  sectionId: "talent",
  sectionTitle: "Talent Acquisition",
  searchKeywords: `
    recruitment hiring  process talent applicants candidates
    equal opportunity equal employment diversity inclusion discrimination harassment
    protected characteristics workplace equality people culture hr 
    interviews assessments reference checks job offer compensation start date
    background checks employment history education certifications verification privacy
    pre-employment onboarding requirements identification government registration tax forms
    employment records employee profile hyvidone documents uploads benefits payroll
    rehire rehiring eligibility performance conduct termination false information
  `,
  sectionBody: (
    <>
      <p>
        At Hyvid, finding and growing great people is at the heart of what we do.
        We hire talented professionals from around the world — people who bring
        curiosity, skill, and character — and give them the freedom and support
        to do their best work.
        We're committed to a hiring process that's fair, transparent, and
        welcoming to everyone.
      </p>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Equal Opportunity
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            We believe opportunity should depend on potential. That's why Hyvid
            provides equal employment opportunities to all applicants and team
            members — regardless of race, colour, religion, gender, sexual
            orientation, gender identity, age, marital status, pregnancy status,
            solo parent status, disability, mental health condition, ethnicity,
            actual or perceived HIV status, tuberculosis, hepatitis B, cancer,
            nationality, or any other status protected by law.
            <strong> We don't tolerate discrimination or harassment of any kind.</strong>
          </p>

          <p>
            Our People &amp; Culture team is always available if you ever
            experience, see, or suspect unequal treatment.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          How We Hire
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-2">
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>
                We look for people who share our values — people who care about
                their craft, communicate openly, and think globally.
              </span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>
                The hiring process will be tailored to each role and may include
                interviews, skills assessments, and reference checks. We'll
                always let you know what to expect and keep communication open
                throughout.
              </span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>
                When you receive an offer, it will clearly state your role,
                compensation, and start date.
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Background Checks
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            To keep our teams and clients safe, we may verify information such
            as employment history, education, or certifications. We handle all
            background checks respectfully and in line with privacy laws.
          </p>

          <p>
            If something doesn't match what was shared during hiring, we'll
            discuss it first — but providing false or misleading information
            can result in withdrawal of an offer or termination of employment.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Pre-Employment Requirements
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Before your first day, you'll need to submit documents that help us
            set you up in our systems. This might include identification,
            government registrations, tax forms, and other onboarding documents.
            All required uploads will be handled via HyvidOne.
            Once everything is submitted, your HyvidOne profile will be complete
            where your employment records, pay, and benefits are managed.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Re-Hiring
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            We love welcoming back great people. If you've previously worked
            with Hyvid and want to return, your eligibility for re-hire will
            depend on your past performance, conduct, and business needs.
            Re-hired employees go through the same onboarding steps as new hires
            so we can get you set up with current systems and policies.
          </p>
        </div>
      </div>
    </>
  ),
},
{
  sectionId: "employment",
  sectionTitle: "Employment Policies and Procedures",
  searchKeywords: `
    employment probationary regular status terms
    working hours schedule shifts meal breaks paid unpaid 
    timekeeping attendance timesheet clock in clock out payroll reporting
    overtime undertime leave requests approvals work location remote remote work
    remote-friendly flexibility accountability professionalism data security public 
    philippine  labor code  pay holiday premium critical workdays
    night shift  differential salary adjustments compensation promotion
    salary increase change  status personnel records employee 
    confidential  government ids emergency contact performance evaluations
    probation confirmation manager expectations benefits compliance documentation
  `,
  sectionBody: (
    <>
      <p>
        At Hyvid, we believe clarity helps everyone do their best work. These policies explain how your employment is structured — from hours and pay to attendance and records — so you always know what's expected and what you can expect in return.
      </p>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Types of Employment
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p className="mb-2">
            We offer different types of engagement based on client needs.
          </p>

          <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>
                <strong>Probationary Employment</strong> – Your first six (6) months help us get to know each other. It's a time to learn, grow, and confirm mutual fit. At the start of your probation, your manager will communicate the specific performance standards and expectations against which you will be assessed. These will be shared in writing via HyvidOne.
              </span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>
                <strong>Regular Employment</strong> – For ongoing roles that are integral to Hyvid's operations. Regular employees receive full statutory and Hyvid-initiated benefits.
              </span>
            </li>
          </ul>

          <p className="mt-2">
            Whatever your status, you're a valued part of the Hyvid community — and we'll always be transparent about your employment terms. All changes to your employment terms will be documented through a formal notice.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Working Hours and Breaks
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p className="mb-2">
            Our standard schedule is <strong>eight (8) hours per day</strong> or <strong>forty (40) hours per week</strong>, not counting meal breaks. Because we work across time zones, your exact hours may vary by client or role. Your manager will confirm your schedule.
          </p>

          <p className="mb-2">You're entitled to:</p>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 dark:border border-emerald-100 dark:border-emerald-800 rounded-lg p-2 text-center">
              <div className="font-bold text-emerald-700 dark:text-emerald-200 text-xs">2 × 15-min paid breaks</div>
              <div className="text-xs muted-text mt-0.5">One in each half of your shift</div>
            </div>

            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg p-2 text-center">
              <div className="font-bold text-emerald-700 dark:text-emerald-200 text-xs">1 × 60-min unpaid break</div>
              <div className="text-xs muted-text mt-0.5">Meal break</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Timekeeping
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            HyvidOne is where you record your time — it keeps everything fair and accurate for payroll and reporting. Log your time in and out each day, review entries before cut-off, and submit any corrections promptly. Overtime, undertime, and leave requests should always be filed and approved in HyvidOne. Managers and People &amp; Culture validate all updates to keep things transparent.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Work Location
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Hyvid is a purely remote-friendly company. Flexibility is a cornerstone of our work environment, balanced with accountability and results. Wherever you work, we expect professionalism, reliability, and secure data practices.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Overtime Work
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Overtime means any authorized work beyond your regular hours. It must be approved beforehand by your manager and recorded in HyvidOne. Authorized overtime is paid according to Labor law.
          </p>

          <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700">
            <CircleAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5"/>
            <div className="text-sm">
              Unapproved overtime can't be paid — always get the green light first.
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Public Holidays
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Hyvid administratively observes all Philippine Official Holidays as prescribed by the Philippines Labor Code or proclaimed by the government. However, taking into consideration the clients that Hyvid is servicing, employees are expected to work through Philippine holidays, and take <strong>client's holidays off</strong>, unless otherwise agreed upon. Holiday pay and premium will be included in the payroll of which the date of the holiday will fall into.
          </p>

          <p>
            These can be considered critical workdays. Failure to report for work during such critical days may be subject to disciplinary action following due process.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Night Work
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Work between <strong>10:00 p.m. and 6:00 a.m.</strong> qualifies as night-shift work. You'll receive a night-differential premium during these hours, as required by law.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Changes to Employment Terms
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            If anything about your employment changes — pay, title, manager, or schedule — we'll document it through a Change of Status Notification and update your record in HyvidOne. You will review and sign the Change of Status before the change takes effect.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Salary Adjustments
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Pay changes can happen for several reasons like promotions and salary alignments. All adjustments go through proper review and approval to ensure fairness and budget alignment. You'll receive a HyvidOne confirmation whenever your salary changes.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Personnel Records
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Hyvid maintains confidential employee records for compliance and continuity. These include your employment documents, evaluations, and any status changes. Keep your personal info current — name, address, emergency contact, and government IDs — by updating them in HyvidOne so we can process your pay and benefits correctly.
          </p>
        </div>
      </div>
    </>
  ),
},
  {
    sectionId: "performance",
    sectionTitle: "Performance Management",
    searchKeywords: `
      performance management  review annual monthly 
      rating system 5-star five-star star  exceptional  
      exceeds expectations meets  needs improvement 
      unsatisfactory goals goal setting success outcomes 
      development professional personal  strengths
      coaching mentoring recognition feedback  history  data
      constructive  continuous  open discussions  documentation  
      standards accountability transparency trust
      offshore manager people culture  responsibilities 
      employee work quality  accuracy productivity timeliness 
      attendance punctuality compliance policies procedures
      professionalism  conduct behaviour behavior  records
      collaboration teamwork leadership initiative reliability contribution impact
      alignment company values client  evidence objective ratings
      discrimination retaliation arbitrary decision making evaluations assessment
      plan pip  corrective action formal   disciplinary action misconduct
      policy violation labor laws documented  recurring issues
      rewards recognition programs incentives promotions salary adjustments
      high performers  planning eligibility support 
      self-reflection self assessment  process hyvidone  
      holistic two-way discussion alignment
      forward momentum ai  fast-changing growth
    `,
    sectionBody: (
      <>
        <p>
          At Hyvid, we believe great performance grows from{" "}
          <strong>clear goals, open feedback, and trust.</strong> Our performance
          process is built to help you understand what success looks like,
          develop your strengths, and achieve your personal and professional
          goals — not just to tick boxes. It's a shared commitment between you
          and your offshore manager, supported by People & Culture through
          HyvidOne.
        </p>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
            Why We Do It
          </h3>

          <div className="pl-4 space-y-1.5">
            <p>Performance management helps us:</p>

            <ul className="space-y-1.5 pl-2">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Align everyone's work with Hyvid's goals and client outcomes.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Recognize great performance and contribution.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Identify opportunities for learning, growth, and leadership.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Support you with feedback and coaching so you can keep progressing.</span>
              </li>
            </ul>

            <p>It's not about judgment — it's about development and shared success.</p>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
            Monthly Performance Rating – 5-Star System
          </h3>

          <div className="pl-4 space-y-3">
          <p>At Hyvid, we believe great work deserves recognition — and growth starts with clear, 
              timely feedback. Our Monthly Performance Rating System is designed to create consistency,
              transparency, and accountability, while supporting ongoing development, recognition, and high performance across all roles.
          <p>Every month, employees receive an overall performance rating from their immediate manager 
              using a simple 5-Star scale. The rating reflects overall performance for the month, taking 
              into account work output, quality, behaviour, collaboration, and alignment with Hyvid's values and policies.
          </p>
          </p>
            {/* 5 Stars */}
            <div className="flex items-start p-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/30">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />

              <div className="px-2">
                <div className="font-semibold title-text text-sm">
                  5 Stars – Exceptional Performance
                </div>
                <div className="text-xs muted-text mt-0.5">
                  You consistently go above and beyond. Your work demonstrates
                  outstanding quality, strong initiative, reliability, collaboration,
                  and a positive impact on the team.
                </div>
              </div>
            </div>

            {/* 4 Stars */}
            <div className="flex items-start p-3 rounded-lg border border-yellow-100 bg-yellow-50/60 dark:border-yellow-800 dark:bg-yellow-900/20">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-gray-200 fill-gray-200" />

              <div className="px-2">
                <div className="font-semibold title-text text-sm">
                  4 Stars – Exceeds Expectations
                </div>
                <div className="text-xs muted-text mt-0.5">
                  You frequently exceed expectations. You deliver high-quality work
                  with minimal supervision and show strong professionalism and accountability.
                </div>
              </div>
            </div>

            {/* 3 Stars */}
            <div className="flex items-start p-3 rounded-lg border border-blue-100 bg-blue-50/40 dark:border-blue-800 dark:bg-blue-900/20">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-gray-200 fill-gray-200" />
              <Star className="w-4 h-4 text-gray-200 fill-gray-200" />

              <div className="px-2">
                <div className="font-semibold title-text text-sm">
                  3 Stars – Meets Expectations
                </div>
                <div className="text-xs muted-text mt-0.5">
                  You consistently meet the requirements of your role. Performance
                  is reliable, dependable, and aligned with expectations.
                </div>
              </div>
            </div>

            {/* 2 Stars */}
            <div className="flex items-start p-3 rounded-lg border border-orange-100 bg-orange-50/40 dark:border-orange-800 dark:bg-orange-900/20">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-gray-200 fill-gray-200" />
              <Star className="w-4 h-4 text-gray-200 fill-gray-200" />
              <Star className="w-4 h-4 text-gray-200 fill-gray-200" />

              <div className="px-2">
                <div className="font-semibold title-text text-sm">
                  2 Stars – Needs Improvement
                </div>
                <div className="text-xs muted-text mt-0.5">
                  Performance is inconsistent or below expectations in one or more areas
                  such as quality, productivity, behaviour, or compliance.
                </div>
              </div>
            </div>

            {/* 1 Star */}
            <div className="flex items-start p-3 rounded-lg border border-red-100 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <Star className="w-4 h-4 text-gray-200 fill-gray-200" />
              <Star className="w-4 h-4 text-gray-200 fill-gray-200" />
              <Star className="w-4 h-4 text-gray-200 fill-gray-200" />
              <Star className="w-4 h-4 text-gray-200 fill-gray-200" />

              <div className="px-2">
                <div className="font-semibold title-text text-sm">
                  1 Star – Unsatisfactory Performance
                </div>
                <div className="text-xs muted-text mt-0.5">
                  Performance is significantly below expectations. Serious or recurring
                  issues are present and immediate corrective action is required.
                </div>
              </div>
            </div>

          </div>
        </div>
        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
            What Managers Consider
          </h3>

          <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
            <p className="mb-2">
              When assigning ratings, managers take a holistic view of performance, including:
            </p>

            <ul className="space-y-1.5 pl-2">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Quality and accuracy of work</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Productivity and timeliness</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Attendance and punctuality</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Compliance with Company policies and procedures</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Professional conduct, collaboration, and teamwork</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Openness to feedback and willingness to improve</span>
              </li>
            </ul>

            <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700 mt-3">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />

              <div className="text-sm">
                <span>
                  No single incident will determine a rating unless it involves{" "}
                  <strong>serious misconduct</strong> or a significant policy violation.
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            Manager Responsibilities
          </h3>

          <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
            <p className="mb-2">
              Managers play a critical role in making this system meaningful and fair. Managers are expected to:
            </p>

            <ul className="list-none space-y-1.5 pl-2">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Apply ratings objectively and based on evidence</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Provide clear, constructive feedback — especially for ratings of 2 Stars or below</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Discuss performance ratings with employees in a timely and respectful manner</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Ensure ratings are never discriminatory, retaliatory, or arbitrary</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            Employee Responsibilities
          </h3>

          <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
            <p className="mb-2">
              You are encouraged to take ownership of your performance by:
            </p>

            <ul className="list-none space-y-1.5 pl-2">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Actively seeking feedback and clarification when needed</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Engaging openly in performance discussions</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Acting on feedback and demonstrating continuous improvement</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            How Ratings Are Used
          </h3>

          <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
            <p className="mb-2">Monthly performance ratings may be used to support:</p>

            <ul className="list-none space-y-1.5 pl-2">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Coaching and development planning</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Performance improvement actions</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Recognition and rewards programs</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Eligibility for incentives, salary adjustments, or promotions (where applicable)</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Identification of high performers and areas needing additional support</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            Low Performance Ratings
          </h3>

          <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
            <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />

              <div>
                <span>
                  A <strong>2-Star rating</strong> may result in targeted coaching or a documented improvement plan.
                  Repeated 2-Star ratings, or any <strong>1-Star rating</strong>, may lead to formal performance
                  management, up to and including disciplinary action, in line with Company policy and applicable labor laws.
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            Annual Performance Review
          </h3>

          <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
            <p>
              At Hyvid, performance is not just about results — it's about growth, impact, and how we continue to evolve
              in a fast-changing, AI-enabled world. The Annual Performance Review is an opportunity to step back, reflect
              on the year that was, and align on what's next.
            </p>

            <p className="mt-2 font-semibold muted-text">The Review Process:</p>

            <ul className="list-none space-y-1.5 pl-2">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Annual reviews are conducted once per year</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Employees are encouraged to complete a self-reflection as part of the process</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Managers will review performance holistically, using documented feedback and results from across the year</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>The review includes a two-way discussion, allowing space for feedback, questions, and alignment on expectations</span>
              </li>
            </ul>

            <p className="mt-2">
              The goal is not just evaluation, but <strong>clarity, growth, and forward momentum.</strong>
            </p>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            Performance Improvement Plan (PIP)
          </h3>

          <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
            <p>
              At Hyvid, a Performance Improvement Plan (PIP) is designed to support team members whose performance or
              behaviour isn't meeting the expected and communicated work standards. Its purpose is to identify where
              improvement is needed and provide structured guidance, coaching, and resources to help every employee succeed.
            </p>

            <p>
              A PIP is a collaborative framework to help you get back on track and reach expectations with confidence.
              If progress doesn't occur after reasonable support, formal action may follow under Hyvid's disciplinary process.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            Documentation
          </h3>

          <div className="pl-4 text-sm muted-text leading-relaxed">
            <p>
              All feedback, reviews, and development plans are recorded in HyvidOne. This ensures transparency,
              accountability, and a record of your growth over time. You'll always have access to your own performance
              data and the opportunity to discuss it with your offshore manager or People & Culture.
            </p>
          </div>
        </div>
      </>
    ),
  },
 {
    sectionId: "remuneration",
    sectionTitle: "Remuneration and Benefits",
    searchKeywords: `
      compensation rewards pay philosophy salary wages payroll remuneration
      equity equal transparency recognition compliance contribution market 
      structure range employment agreement review annual payslip bank transfer
      schedule cutoff payout enrollment coverage provider insurance dependents 
      electronic payday working days withholding tax government healthcare  
      deductions authorized adjustments overtime labor laws social protection
      government-mandated benefits statutory maintenance organization credits   
      hyvid-initiated employee  wellbeing wellness work-life balance leave sick
      paid time off pto vacation hmo health gynecological surgery hospitalization   
      accrual carry over cash conversion  anniversary separation clearance office
      notice period resignation  shutdown rehire eligibility magna carta 9710 
      request advance  business needs  approval supporting documents clinic record police 
      illness injury medical certificate return to work form benchmarks recovery
      report absence bereavement  family death funeral grieving special confidentiality   
      immediate  spouse parent child sibling grandparent uncle aunt nephew niece cousin
      vawc violence against women children republic act 9262 barangay  order 
    `,
    sectionBody: (
      <>
        <div className="space-y-4 text-sm muted-text leading-relaxed">
          <p>
            At Hyvid, we believe great work deserves fair and transparent rewards. We want everyone to understand how pay, benefits, and incentives work — and to know that they're built on respect, compliance, and trust. Our approach to compensation is simple: <strong>be fair, be clear, and recognize contribution.</strong>
          </p>

          <div className="mb-4">
            <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Our Pay Philosophy
            </h3>

            <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p className="mb-2">
                Your pay reflects your skills, responsibilities, performance, and market conditions. We use consistent frameworks so similar roles are paid fairly, wherever you're based. Our pay philosophy rests on four pillars:
              </p>

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg p-2.5">
                  <div className="font-semibold text-purple-700 dark:text-purple-400 text-xs">Equity</div>
                  <div className="text-xs muted-text mt-0.5">Equal pay for equal work</div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg p-2.5">
                  <div className="font-semibold text-purple-700 dark:text-purple-400 text-xs">Transparency</div>
                  <div className="text-xs muted-text mt-0.5">Clear communication about how pay and benefits are structured</div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg p-2.5">
                  <div className="font-semibold text-purple-700 dark:text-purple-400 text-xs">Compliance</div>
                  <div className="text-xs muted-text mt-0.5">Full alignment with local Labor laws and any client-country requirements</div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg p-2.5">
                  <div className="font-semibold text-purple-700 dark:text-purple-400 text-xs">Recognition</div>
                  <div className="text-xs muted-text mt-0.5">Rewarding performance, impact, and initiative</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Pay Structure
            </h3>
            <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p>
                Each role at Hyvid has a defined pay range based on its scope, market benchmarks, and client billing structures. Salary offers are made in writing before you start and confirmed in your Employment Agreement. We review salaries annually to stay competitive and to recognize high performance.
              </p>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Pay Schedule
            </h3>

            <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p className="mb-2">
                You'll receive your pay <strong>twice a month</strong>, via electronic transfer to your registered bank account. Your pay is calculated on the following schedule:
              </p>

              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                      <th className="px-3 py-2 text-left font-semibold">Cut-off Period</th>
                      <th className="px-3 py-2 text-left font-semibold">Pay Out Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-gray-100 dark-mode-card">
                      <td className="px-3 py-2 align-top">1st – 15th of the month</td>
                      <td className="px-3 py-2 align-top">25th of the month</td>
                    </tr>
                    <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                      <td className="px-3 py-2 align-top">16th to end of the month</td>
                      <td className="px-3 py-2 align-top">10th of the following month</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="mt-2 text-xs text-gray-500">
                Payslips are available through HyvidOne. If payday falls on a weekend or public holiday, you'll be paid on the nearest working day. If you ever notice an issue with your pay, raise a request with People &amp; Culture via HyvidOne within five (5) working days — we'll sort it out quickly.
              </p>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Deductions
            </h3>

            <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p className="mb-2">Deductions are made only when required by law or authorized by you. These may include:</p>

              <ul className="list-none space-y-1.5 pl-2">
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  Government contributions
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  Withholding taxes
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  Approved salary deductions
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  Authorized adjustments (e.g., lost or damaged company property, unpaid absences or lost hours)
                </li>
              </ul>

              <p className="text-xs text-gray-500 mt-1">
                All deductions are listed clearly on your payslip for full transparency.
              </p>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Overtime Pay
            </h3>
            <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p>
                If you qualify, and you work overtime that's been approved in advance, you'll receive pay in line with your applicable country's labor laws. Unapproved overtime isn't paid, so always confirm with your offshore manager first.
              </p>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Government-Mandated Benefits
            </h3>
            <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p>
                Hyvid is committed to full compliance with all applicable Labor laws and regulations in every country where we operate. We provide all government-mandated benefits required by law, including statutory contributions, and social protection benefits, in accordance with local requirements. These benefits form part of our commitment to responsible employment practices and to supporting the well-being and security of our people as we build the future of work together.
              </p>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Hyvid-Initiated Benefits
            </h3>
            <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p>
                At Hyvid, we recognise that every person brings energy, focus, and care to their work when they feel supported and valued. Beyond government-mandated benefits, Hyvid offers a suite of company-initiated programs designed to enhance wellbeing, reward commitment, and promote a healthy balance between life and work. These benefits may vary depending on your role, client engagement, location, and tenure, but the principles remain the same — <strong>care, consistency, and fairness.</strong>
              </p>
            </div>
          </div>

          <div class="mb-4">
            <h3 class="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Paid Time Off (PTO)
            </h3>

            <div class="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p class="mb-3">
                Hyvid provides paid time off (PTO) to recognise performance, tenure, or to promote wellness. This benefit acknowledges that time away from work supports long-term productivity and overall wellbeing.
              </p>

              <div class="grid grid-cols-2 gap-3 mb-3">
                <div class="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg p-3 text-center">
                  <div class="text-3xl font-bold text-emerald-600 dark:text-emerald-500">15</div>
                  <div class="text-xs muted-text mt-1">Vacation Leave Days</div>
                </div>

                <div class="bg-blue-50 border border-blue-100 dark:bg-blue-900/20 border dark:border-blue-800 rounded-lg p-3 text-center">
                  <div class="text-3xl font-bold text-blue-600 dark:text-blue-500">5</div>
                  <div class="text-xs muted-text mt-1">Sick Leave Days</div>
                </div>
              </div>

              <p class="mb-2">
                PTO is earned on a monthly basis across your service year. You will be eligible for your PTO as long as you have worked or were on paid leave, for at least half the working days in that month.
              </p>

              <p class="mb-2 font-semibold muted-text">
                The following guidelines apply to the use, carry-over, and conversion to cash of leave credits:
              </p>

              <ul class="list-none space-y-1.5 pl-2">
                <li class="flex items-start gap-2">
                  <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  <span><strong>Unused Leave Credits:</strong> 10 unused Vacation Leave (VL) credits by your employment anniversary date will be carried over in the next service year. Anything in excess of 10 will be converted to cash. All unused Sick Leaves will be forfeited by your employment anniversary date.</span>
                </li>

                <li class="flex items-start gap-2">
                  <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  <span><strong>PTO Conversion on Separation:</strong> Team members who are eligible for PTO and are separated from Hyvid will receive cash conversion of all unused PTO credits, provided all clearance procedures are completed.</span>
                </li>

                <li class="flex items-start gap-2">
                  <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  <span><strong>Use of PTO During Notice Period:</strong> PTO credits cannot be used during the resignation notice period to offset required working days unless otherwise approved by management.</span>
                </li>

                <li class="flex items-start gap-2">
                  <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  <span><strong>Rehire Eligibility:</strong> Employees who are rehired will begin with a new leave accrual cycle and are not entitled to previously accrued PTO balances from prior employment with Hyvid.</span>
                </li>

                <li class="flex items-start gap-2">
                  <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  <span>Management may direct you at its discretion to use your vacation leave credits in situations such as on office shut down.</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="mb-4">
            <h3 class="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Vacation Leave
            </h3>

            <div class="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p>
                Vacation Leave requests must be filed in HyvidOne and approved by your manager at least <strong>thirty (30) days</strong> before the intended leave date, unless for urgent reasons. If multiple team members within the same function apply for overlapping periods, approvals will be based on business needs and order of filing.
              </p>
            </div>
          </div>

          <div class="mb-4">
            <h3 class="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Sick Leave
            </h3>

            <div class="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p>
                If you are unable to report for work due to illness or injury, please notify your immediate manager at least <strong>one (1) hour</strong> before your scheduled shift. Notification should be made through approved communication channels (e.g., HyvidOne, email, or direct message). Acknowledgment of your message confirms receipt but does not automatically mean approval of the leave.
              </p>

              <p class="mt-2 font-semibold muted-text">Documentation and Return to Work:</p>
              <p>
                Upon returning to work, you must complete and submit a Leave Form in HyvidOne to formalize your absence. The reason for the absence must be valid and substantial, as determined by your supervisor.
              </p>

              <p class="mt-2 font-semibold muted-text">Supporting Documents:</p>
              <p>
                Your supervisor or People &amp; Culture may request supporting documentation such as a medical certificate, clinic record, or relevant report (e.g., police report for accidents). A medical certificate is required for absences of <strong>two (2) or more consecutive working days</strong> due to illness.
              </p>

              <p class="mt-2">
                Hyvid encourages everyone to prioritise their health and take appropriate rest when unwell. Our goal is to support your recovery while ensuring transparency and smooth coordination within the team.
              </p>
            </div>
          </div>

          <div class="mb-4">
            <h3 class="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Bereavement Leave
            </h3>

            <div class="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p class="mb-2">
                At Hyvid, we understand that losing a loved one is an incredibly difficult time. We want to ensure our people have the space to grieve, support their families, and manage personal matters without additional stress.
              </p>

              <ul class="list-none space-y-1.5 pl-2">
                <li class="flex items-start gap-2">
                  <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  <span><strong>3 days</strong> of paid bereavement leave for the death of an immediate family member, including a spouse, child, parent, or sibling.</span>
                </li>

                <li class="flex items-start gap-2">
                  <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                  <span><strong>1 day</strong> of paid bereavement leave for the death of a distant family member, such as a grandparent, uncle, aunt, nephew, niece, or cousin.</span>
                </li>
              </ul>

              <p class="mt-2">
                To ensure timely support and accurate processing, please complete the Bereavement Leave process in HyvidOne providing: the name of the deceased, relationship to the employee, dates of requested leave, and supporting documentation such as a death certificate or funeral notice.
              </p>

              <p class="mt-1">
                Hyvid recognises that each person grieves differently. People &amp; Culture is available to provide guidance, flexibility, and access to additional support if needed during this time.
              </p>
            </div>
          </div>

          <div class="mb-4">
            <h3 class="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              VAWC Leave
            </h3>

            <div class="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p>
                Under the Anti-Violence Against Women and Their Children Act (Republic Act No. 9262), any employee who is a victim of violence against women or their children is entitled to <strong>ten (10) days of paid VAWC leave per year</strong> to attend to legal proceedings, medical treatment, or other needs directly arising from the violence experienced. This leave is non-cumulative and cannot be converted to cash. To apply, contact People &amp; Culture confidentially. Supporting documentation (such as a barangay protection order or medical record) may be requested. All information is treated with strict confidentiality.
              </p>
            </div>
          </div>

          <div class="mb-4">
            <h3 class="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Special Leave for Women (Gynecological)
            </h3>

            <div class="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p>
                Under the Magna Carta of Women (Republic Act No. 9710), a female employee who has rendered continuous aggregate employment of at least six (6) months is entitled to <strong>special leave of up to two (2) months with full pay</strong> following surgery caused by a gynecological disorder requiring hospitalisation. A medical certificate from a licensed physician is required. Contact People &amp; Culture to apply.
              </p>
            </div>
          </div>

          <div class="mb-4">
            <h3 class="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              HMO Coverage
            </h3>

            <div class="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
              <p>
                At Hyvid, we care deeply about your health and well-being. As part of our commitment to supporting you — both at work and beyond — the Company provides Health Maintenance Organization (HMO) coverage to eligible employees. Enrollment will be under an HMO program selected by the Company, with coverage and benefits governed by the terms of the provider.
              </p>

              <p>
                Specific details on eligibility, coverage levels, enrollment timelines, and dependent options (where applicable) will be shared separately. As our business and workforce continue to evolve, the Company may update or change its HMO offerings in line with business needs and applicable laws.
              </p>
            </div>
          </div>
        </div>
        
      </>
    ),
  },
  {
  sectionId: "separation",
  sectionTitle: "Separation and Employment Clearance",
  searchKeywords: `
    resignation resign voluntary letter written notice period 30-day
    last working day turnover handover knowledge transfer responsibilities
    transition professionalism mutual respect hyvidone submission documents
    paid time-off pto leave credits  waiver management approval entitlements
    clearance employment  exit  final pay separation requirements equipment
    company property client materials liquidated damages contract endorsement
    work compliance documentation labor laws operational needs policy agreement   
    termination termination just cause authorized  due process handbook breach
    dismissal wages earned exit attendance records timer eturn assets company-issued
  `,
  sectionBody: (
    <>
      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Resignation
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            At Hyvid, we value professionalism and mutual respect when a team member decides to move on. To ensure a smooth transition for both the individual and the business, the following procedures apply.
          </p>

          <p className="mt-2 font-semibold muted-text">Notice Period:</p>

          <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>
                Team members who wish to resign are required to submit a written notice of resignation at least <strong>thirty (30) days</strong> before their intended last working day.
              </span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>The written notice must be submitted through HyvidOne.</span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>
                During the 30-day notice period, employees are expected to continue reporting for work and completing the proper turnover of tasks and responsibilities.
              </span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>
                Unused Paid Time Off (PTO) or other leave credits cannot be used to offset or shorten the required notice period unless otherwise approved by management in writing.
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Turnover and Compliance Requirements
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed">
          <p>
            All work documents, company-issued equipment, and client materials must be properly endorsed before your final day. Failure to render the required notice, complete the proper turnover, or submit required documentation may result in the forfeiture of final pay and the application of liquidated damages as specified in your employment contract and relevant labour laws. The final pay release will occur only after full clearance is secured through the Hyvid Employment Clearance Process.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Waiver of Notice
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Hyvid reserves the right, at its sole discretion, to <strong>waive all or part of the 30-day notice period</strong> based on operational needs or other considerations. In such cases, the employee's resignation will still be processed as voluntary, and any earned entitlements will be settled accordingly.
          </p>

          <p>
            At Hyvid, we believe that every departure should be handled with respect and professionalism. Our goal is for every team member to leave the organisation on positive terms, with their contributions valued and appreciated.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Termination
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            At Hyvid, we believe that employment should always be based on fairness, respect, and transparency. Ending employment is never taken lightly — it's done only after careful consideration and in full compliance with the <strong>Labor Code</strong>, your <strong>Employment Agreement</strong>, and this Handbook.
          </p>

          <p>
            Employment may be ended for <strong>just causes</strong>, <strong>authorized causes</strong>, or breaches of company policies or agreements, but always with proper process and documentation.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Final Pay and Clearance
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed">
          <p>
            When your employment ends, your <strong>final pay</strong> will include all earned wages, benefits, and any unused leave credits (as applicable). Final pay is released within <strong>30 days</strong> of your last working day, provided you've completed the clearance process. This includes returning company property, completing exit documents, and confirming final attendance and time records.
          </p>
        </div>
      </div>
    </>
  ),
},
{
  sectionId: "health-safety",
  sectionTitle: "Health, Safety & Compliance",
  searchKeywords: `
    health safety wellbeing workplace occupational seminar onboarding   
    work from home remote office workspace setup ergonomics incident
    dole reporting incidents injuries accident medical certificate
    right disconnect mental wellness drug free illegal drugs confidential
    controlled substances prohibited possession manufacture distribution 
    substance abuse dependence disciplinary action smoke smoking vaping
    alcohol policy impairment harassment safe spaces anti discrimination
    bullying peer conduct investigation complaint zero tolerance respectful 
    11313 government guidelines authority compliance mandatory training  
    data privacy information security responsible technology ai readiness
    continued employment acceptable  passwords mfa multi factor authentication
    vpn screen locking monitoring social media requirements infractions
    internet web browsing offensive content pornographic unethical company 
    excessive personal email business communication authorized disclosure 
    professional client property artificial intelligence approved tools 
    platforms deliverables financial records act 10173 generated review 
    obligations misuse governance  confidentiality breach npc national 
    commission legal requirement duty conflict interest conflicts competitor 
    resources gifts payments favours ethics ethical transparency lost    
    equipment negligence damaged ost recovery return assets public 
    representation communications marketing online work secure standards 
    devices video calls lock dress code  casual appearance  meetings polo 
    shirts blouses slacks khakis jeans loafers sneakers sandals attire 
    ripped tattered vulgar prints derogatory slogans revealing clothing   
  `,
  sectionBody: (
    <>
      <div className="flex gap-3 p-3 rounded-lg border bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700">
        <CircleCheckBig className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <span>
            At Hyvid, your health, safety, and wellbeing matter. We're committed to creating a safe, respectful, and inclusive work environment — wherever you work from. Everyone shares responsibility for maintaining a healthy and safe workplace.
          </span>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Working Safely from Home
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Hyvid's full <strong>Occupational Safety, Health & Wellbeing Policy</strong> sets out everything you need to know about working safely from home — including your workspace setup, the mandatory DOLE OSH seminar, ergonomics, mental health, incident reporting, and your right to disconnect. Read it in HyvidOne and acknowledge it as part of your onboarding. If you have any questions about your setup, reach out to People & Culture.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Drug-Free Workplace
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            At Hyvid, we're committed to keeping our workplace safe, healthy, and focused. That's why the use, possession, sale, manufacture, or distribution of illegal drugs or controlled substances is <strong>strictly prohibited</strong> anywhere connected to work — whether onsite, remote, or at any company-related event. No one should work under the influence of drugs or any prohibited substance. Breaking this policy is serious and may lead to disciplinary action, including termination of employment.
          </p>

          <p>
            If you or someone you know is struggling with substance use or dependence, please reach out to People & Culture — early, privately, and without judgment. You can also report violations directly and confidentially to People & Culture.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Smoke-Free and Alcohol Policy
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Smoking, vaping, and alcohol use aren't permitted during working hours or in any way that affects your performance, professionalism, or safety. If you're representing Hyvid or working on client projects, you're expected to maintain a clear, professional state of mind at all times.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Harassment-Free Workplace
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Hyvid has a <strong>zero-tolerance</strong> approach to harassment of any kind. Hyvid's full <strong>Safe Spaces & Anti-Harassment Policy</strong> covers what counts as harassment (including online and peer-to-peer conduct under RA 11313), how to report it, how investigations work, and what happens to people who breach the policy. Read it in HyvidOne and acknowledge it as part of your onboarding. If you experience or witness harassment, report it to People & Culture or the CODI immediately — both contacts are available in HyvidOne.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Workplace Incidents or Injuries
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            If you're injured or experience an accident while working — even remotely — tell your manager and <strong>People & Culture</strong> right away. We'll document and investigate what happened so we can prevent it in the future. You may be asked to provide a medical certificate or short incident report for our records.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Public Health and Emergencies
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            During health crises or pandemics, Hyvid follows official government and health authority guidelines. We may temporarily adjust work arrangements, introduce additional safety measures, or require health declarations or medical documentation when necessary. Your wellbeing and compliance with legal requirements are always our top priorities.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Compliance and Mandatory Training
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p className="mb-2">
            All Hyvid team members must complete compliance training programs, including those on:
          </p>

          <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Data privacy and information security</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Workplace safety</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Anti-harassment and discrimination</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Responsible use of technology</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>AI readiness</span>
            </li>
          </ul>

          <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700">
            <CircleAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span>
                Completion of these modules is a <strong>condition of continued employment.</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          IT Acceptable Use
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Hyvid's full <strong>Work-from-Home, Data Privacy & IT Use Policy</strong> covers acceptable and prohibited use of Hyvid systems, security requirements (passwords, MFA, VPN, screen locking), monitoring, social media, and what happens when your employment ends. The disciplinary offence tables in the Code of Conduct below remain the definitive reference for specific IT infractions and their consequences. Read the full policy in HyvidOne and acknowledge it as part of your onboarding.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Internet Use
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            The internet is a powerful tool for research, collaboration, and communication. However, visiting websites or viewing content that's pornographic, offensive, illegal, or unrelated to work is strictly prohibited. Using Hyvid's network for inappropriate, unethical, or excessive personal browsing may lead to disciplinary action — up to and including termination of employment.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Email Use
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p className="mb-2">
            If you utilise either a Hyvid or client email it should be used for business-related communication only.
          </p>

          <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Keep messages professional and respectful.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Don't share confidential or client information outside Hyvid without approval.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Avoid using your work email for personal business or non-work matters.</span>
            </li>
          </ul>

          <p className="mt-1">
            Sending, forwarding, or storing offensive, pornographic, or harassing material (including jokes or images) is strictly prohibited and will be treated as a serious violation under Hyvid's Harassment-Free Workplace Policy. Revealing client information — even if it seems minor — can compromise trust and security. Always treat both client and company data as confidential.
          </p>

          <p className="mt-1">
            All emails and files created, sent, or stored on Hyvid systems are company property. Management and authorised personnel may access these systems when required for business or compliance reasons.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          AI Use
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p className="mb-2">
            AI is a big part of how Hyvid works — but with great power comes great responsibility. You're encouraged to use AI tools to boost your productivity and creativity, as long as you do so ethically and securely. These are the rules that apply to everyone:
          </p>

          <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span><strong>Use only approved tools.</strong> Use only AI platforms that have been approved by Hyvid or explicitly approved by your client. Do not use personal AI subscriptions for work tasks without approval.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span><strong>Never input confidential data.</strong> Do not input client data, personal data about employees or third parties, financial records, or other confidential information into any AI tool unless it operates within a secure, approved environment that complies with the Data Privacy Act (RA 10173) and any applicable client data agreements.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span><strong>Own your output.</strong> You are responsible for the quality and accuracy of anything you produce, whether generated with AI assistance or not. Always review AI-generated content before sharing it with a client, manager, or colleague. Submitting AI output without review is not acceptable.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span><strong>Disclose AI use when relevant.</strong> Some clients or projects require disclosure when AI tools are used in producing deliverables. Follow any specific guidance your manager provides on this. When in doubt, disclose.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span><strong>Report misuse or concerns.</strong> If you become aware of AI being used in a way that may breach client confidentiality, data privacy obligations, or Hyvid's policies, report it to People & Culture or your manager immediately.</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Confidentiality and Data Privacy
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Hyvid's full <strong>Work-from-Home, Data Privacy & IT Use Policy</strong> sets out your obligations under the Data Privacy Act (RA 10173), how to handle personal and client data, what to do if you suspect a data breach (including the 72-hour NPC reporting requirement), and how Hyvid handles your own personal data. Read the full policy in HyvidOne. This is part of your professional duty and a legal requirement.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Conflicts of Interest
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p className="mb-2">
            We all make better decisions when our actions are free from personal conflict. Avoid situations where your interests could compete with Hyvid's — such as:
          </p>

          <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Working for a direct competitor.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Using company resources for personal projects.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Accepting gifts, payments, or favours that could influence your work.</span>
            </li>
          </ul>

          <p className="mt-1">
            If you're unsure whether something might be a conflict, disclose it to People & Culture so it can be reviewed transparently.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Use of Company Property and Resources
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Hyvid's tools, equipment, and systems are provided so you can do your best work. Treat them with care and use them responsibly — for authorized business use only. If company equipment is lost or damaged through negligence, we may ask for cost recovery as outlined in policy. All company-issued items must be returned in good condition when your employment ends.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Social Media and Public Representation
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            We encourage you to celebrate your work and share your career journey — responsibly. When posting online, remember: you represent yourself and Hyvid. Avoid sharing confidential information or speaking on behalf of the company unless authorized. Be respectful, positive, and professional in all public communications. If you're unsure, check with <strong>Marketing & Communications</strong> or <strong>People & Culture</strong> before posting.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Remote Work Environment Standards
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>
            Hyvid's full <strong>Work-from-Home, Data Privacy & IT Use Policy</strong> covers all remote work environment standards in detail, including workspace setup, equipment, security, privacy during video calls, and professional conduct expectations. A quick summary: keep your workspace private and secure, use only approved devices and tools, lock your screen when you step away, and present yourself professionally on client calls. Full details are in HyvidOne.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Dress and Presentation
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p className="mb-3">
            We're a flexible, modern workplace — but professionalism still matters. Whether you're working remotely, visiting clients, or joining virtual meetings, present yourself appropriately for your role and audience. When meeting clients or representing Hyvid, business-casual attire is generally expected.
          </p>

          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                  <th className="px-3 py-2 text-left">Gender</th>
                  <th className="px-3 py-2 text-left text-emerald-100">✓ Allowed</th>
                  <th className="px-3 py-2 text-left text-red-200">✗ Prohibited</th>
                </tr>
              </thead>

              <tbody>
                <tr className="border-t border-gray-100 align-top">
                  <td className="px-3 py-2 font-semibold muted-text">Male</td>
                  <td className="px-3 py-2 text-emerald-700 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/20">
                   Polo/golf shirts, short/long-sleeved shirts, round/V-neck shirts, suits; 
                   tailored pants, slacks, khakis, jeans/denims, cargo/carpenter pants; leather shoes, 
                   loafers/suedes, canvas shoes, sneakers, athletic shoes
                  </td>
                  <td className="px-3 py-2 text-red-700 dark:text-red-200 bg-red-50 dark:text-red-200 dark:bg-red-900/400">
                    Shirts with vulgar prints (sex, alcohol, drugs) or derogatory slogans; sandos/sleeveless 
                    shirts; worn out, tattered, cut-off or ripped clothing; short pants; all forms of slippers and sandals
                  </td>
                </tr>

                <tr className="border-t border-gray-100 align-top bg-gray-50 dark:bg-gray-900/50">
                  <td className="px-3 py-2 font-semibold muted-text">Female</td>
                  <td className="px-3 py-2 text-emerald-700 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/20">
                    Collared/round/V-neck shirts, blouses, dresses, sleeveless blouses; tailored pants,
                     slacks, khakis, jeans/denims, cargo/carpenter pants, skirts of appropriate length, 
                     leggings; leather shoes, loafers/suedes, canvas shoes, sneakers, athletic shoes
                  </td>
                  <td className="px-3 py-2 text-red-700 dark:text-red-200 bg-red-50 dark:text-red-200 dark:bg-red-900/400">
                    Shirts with vulgar prints or derogatory slogans; revealing blouses or provocative clothing; worn out, 
                    tattered, cut-off or ripped clothing; short pants; revealing or provocative skirts; all forms of slippers
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
    </>
  ),
},
{
  sectionId: "code-of-conduct",
  sectionTitle: "Code of Conduct",
  searchKeywords: `
    accountability workplace discipline disciplinary action fairness 
    transparency consistency principles expectations consequences 
    confidentially confidentiality labor laws offense offenses   
    corrective severity seriousness minor moderate serious grave 
    class classification misconduct misdemeanor detrimental impact 
    financial loss employee security physical harm integrity judgment
    disorder productivity effectiveness client satisfaction operations 
    operational delay missed opportunities compromise safety hazard 
    injury public perception program department disruption severe disruption
    data system information endanger proportionate circumstances code of conduct      
    infraction infractions classes respect labor laws due process hearing 
    investigation warning verbal written first final dismissal termination 
    prescriptive period  history repeat attendance punctuality no call show ncns
    return work order rtwo absenteeism unauthorized absence certificate medical
    tardiness late reporting health compliance policy violation handbook behavior 
    classification offense categories action actions warnings call rtwo sickness
    schedule shift company property assets equipment misuse negligence damage loss
    dishonesty integrity fraud theft falsification misrepresentation breach email
    behaviour professionalism respect harassment misconduct safety security ai   
    technology acceptable use internet privacy conflict interest social media
    remote from home dress presentation
  `,
  sectionBody: (
    <>
      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Principles We Follow
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p>At Hyvid, we believe in accountability that's fair and consistent. To help everyone understand expectations and the consequences of certain behaviours,
            we classify workplace offenses based on their <strong>level of seriousness.</strong></p>
          <p className="mb-2">Our approach to discipline is guided by three principles:</p>

          <div className="grid grid-cols-3 gap-2">
            <div className="dark-mode-card border border-gray-100 rounded-lg p-2.5">
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-400 to-purple-400 text-white text-xs font-bold flex items-center justify-center mb-1">
                1
              </div>
              <div className="font-semibold title-text text-xs">Fairness</div>
              <div className="text-xs muted-text mt-0.5">
                Every team member has the right to be heard and treated with respect.
              </div>
            </div>

            <div className="dark-mode-card border border-gray-100 rounded-lg p-2.5">
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-400 to-purple-400 text-white text-xs font-bold flex items-center justify-center mb-1">
                2
              </div>
              <div className="font-semibold title-text text-xs">Transparency</div>
              <div className="text-xs muted-text mt-0.5">
                We communicate clearly about the issue, expectations, and next steps.
              </div>
            </div>

            <div className="dark-mode-card border border-gray-100 rounded-lg p-2.5">
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-400 to-purple-400 text-white text-xs font-bold flex items-center justify-center mb-1">
                3
              </div>
              <div className="font-semibold title-text text-xs">Consistency</div>
              <div className="text-xs muted-text mt-0.5">
                Similar situations are handled consistently across the company.
              </div>
            </div>
          </div>

          <p className="mt-2 text-xs text-gray-500">
            All disciplinary actions are handled confidentially and in accordance with labor laws.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Offense Classification
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p className="mb-3">
            Offenses are grouped into four classes — from minor to grave — to ensure that corrective actions are fair, consistent, and proportionate to the situation. Hyvid reserves the right to determine the appropriate classification depending on the specific circumstances of each case.
          </p>

          <div className="grid grid-cols-2 gap-2">
            <div className="p-3 rounded-lg border bg-green-100 dark:bg-green-800 border-green-200 text-green-800 dark:bg-green-900/70 dark:border-green-700 dark:text-green-100">
              <div className="font-bold text-sm mb-2">Class A – Minor Offenses</div>
              <ul className="space-y-1">
                <li className="text-xs flex gap-1.5"><span>•</span><span>Have no detrimental impact on the business and are considered light misdemeanors.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Do not cause financial loss.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Have minimal impact on clients, Hyvid, or employee security.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Do not cause physical harm, but may contribute to disorder.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Do not call into question the employee's integrity.</span></li>
              </ul>
            </div>

            <div className="p-3 rounded-lg border bg-yellow-100 dark:bg-yellow-800 border-yellow-200 text-yellow-800 dark:bg-yellow-800/70 dark:border-yellow-700 dark:text-yellow-100">
              <div className="font-bold text-sm mb-2">Class B – Moderate Offenses</div>
              <ul className="space-y-1">
                <li className="text-xs flex gap-1.5"><span>•</span><span>May delay operations, affect productivity, or result in missed opportunities.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Can compromise team effectiveness or client satisfaction.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>May cause considerable financial loss.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Have a moderate impact on security.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Could create a potential for injury or pose a moderate safety hazard.</span></li>
              </ul>
            </div>

            <div className="p-3 rounded-lg border bg-orange-100 dark:bg-orange-800 border-orange-200 text-orange-800 dark:bg-orange-800/70 dark:border-orange-700 dark:text-orange-100">
              <div className="font-bold text-sm mb-2">Class C – Serious Offenses</div>
              <ul className="space-y-1">
                <li className="text-xs flex gap-1.5"><span>•</span><span>Have a significant impact on business operations or public perception of Hyvid.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>May result in financial compromise within a program or department.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Place a serious doubt on the employee's integrity or judgment.</span></li>
              </ul>
            </div>

            <div className="p-3 rounded-lg border bg-red-100 dark:bg-red-800 border-red-200 text-red-800 dark:bg-red-800/70 dark:border-red-700 dark:text-red-100">
              <div className="font-bold text-sm mb-2">Class D – Grave Offenses</div>
              <ul className="space-y-1">
                <li className="text-xs flex gap-1.5"><span>•</span><span>Involve physical harm to oneself or others.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Cause severe disruption to business operations.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Result in significant financial loss to the company.</span></li>
                <li className="text-xs flex gap-1.5"><span>•</span><span>Compromise client information, data integrity, or system security, or endanger the safety of other employees.</span></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Prescriptive Periods & Corrective Actions
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p className="mb-3 text-xs muted-text">
            The prescriptive period refers to the length of time a corrective action remains active. If the prescriptive period passes and no similar infraction has been committed, the previous corrective action is considered cleared.
          </p>

          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-green-700 dark:text-green-200 mb-1.5">
                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-800 rounded-full">Class A</span> Minor Offenses
              </div>

              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                      <th className="px-3 py-2 text-left font-semibold"># of Offense</th>
                      <th className="px-3 py-2 text-left font-semibold">Corrective Action</th>
                      <th className="px-3 py-2 text-left font-semibold">Prescriptive Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-gray-100 dark-mode-card">
                      <td className="px-3 py-2">1st Offense</td>
                      <td className="px-3 py-2">Verbal Warning</td>
                      <td className="px-3 py-2">3 months</td>
                    </tr>
                    <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                      <td className="px-3 py-2">2nd Offense</td>
                      <td className="px-3 py-2">1st Written Warning</td>
                      <td className="px-3 py-2">6 months</td>
                    </tr>
                    <tr className="border-t border-gray-100 dark-mode-card">
                      <td className="px-3 py-2">3rd Offense</td>
                      <td className="px-3 py-2">Final Warning</td>
                      <td className="px-3 py-2">12 months</td>
                    </tr>
                    <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                      <td className="px-3 py-2">4th Offense</td>
                      <td className="px-3 py-2">Dismissal</td>
                      <td className="px-3 py-2">Not applicable</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-yellow-700 dark:text-yellow-200 mb-1.5">
              <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-800 rounded-full">Class B</span> Moderate Offenses
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                    <th className="px-3 py-2 text-left font-semibold"># of Offense</th>
                    <th className="px-3 py-2 text-left font-semibold">Corrective Action</th>
                    <th className="px-3 py-2 text-left font-semibold">Prescriptive Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100 dark-mode-card">
                    <td className="px-3 py-2 align-top">1st Offense</td>
                    <td className="px-3 py-2 align-top">1st Written Warning</td>
                    <td className="px-3 py-2 align-top">6 months</td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                    <td className="px-3 py-2 align-top">2nd Offense</td>
                    <td className="px-3 py-2 align-top">Final Written Warning</td>
                    <td className="px-3 py-2 align-top">12 months</td>
                  </tr>
                  <tr className="border-t border-gray-100 dark-mode-card">
                    <td className="px-3 py-2 align-top">3rd Offense</td>
                    <td className="px-3 py-2 align-top">Dismissal</td>
                    <td className="px-3 py-2 align-top">Not applicable</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-orange-700 dark:text-orange-200 mb-1.5">
              <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-800 rounded-full">Class C</span> Serious Offenses
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                    <th className="px-3 py-2 text-left font-semibold"># of Offense</th>
                    <th className="px-3 py-2 text-left font-semibold">Corrective Action</th>
                    <th className="px-3 py-2 text-left font-semibold">Prescriptive Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100 dark-mode-card">
                    <td className="px-3 py-2 align-top">1st Offense</td>
                    <td className="px-3 py-2 align-top">Final Warning</td>
                    <td className="px-3 py-2 align-top">12 months</td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                    <td className="px-3 py-2 align-top">2nd Offense</td>
                    <td className="px-3 py-2 align-top">Dismissal</td>
                    <td className="px-3 py-2 align-top">Not applicable</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-red-700 dark:text-red-200 mb-1.5">
              <span className="px-2 py-0.5 bg-red-100 dark:bg-red-800 rounded-full">Class D</span> Grave Offenses
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                    <th className="px-3 py-2 text-left font-semibold"># of Offense</th>
                    <th className="px-3 py-2 text-left font-semibold">Corrective Action</th>
                    <th className="px-3 py-2 text-left font-semibold">Prescriptive Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100 dark-mode-card">
                    <td className="px-3 py-2 align-top">1st Offense</td>
                    <td className="px-3 py-2 align-top">Dismissal</td>
                    <td className="px-3 py-2 align-top">Not applicable</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
        </div>
      </div>
      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          Categories of Offenses
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <p className="mb-3">
            To keep things clear and consistent, offenses are grouped by type. The main categories are: Attendance, Punctuality &amp; Productivity; Property and Assets; Dishonesty and Integrity; Conduct and Behaviour; Health, Safety and Security; and Technology and Acceptable Use.
          </p>

          <div className="space-y-3">

            <details
              className="group rounded-lg border border-gray-200 dark:border-gray-700 dark-mode-card overflow-hidden"
            >
              <summary className="flex items-center justify-between px-3 py-2.5 cursor-pointer font-semibold muted-text text-xs hover:bg-gray-50 dark:hover:bg-gray-900/50">
                <span>Attendance, Punctuality and Productivity</span>

                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180"/>
              </summary>

              <div className="px-3 pb-3 space-y-3">

                <p className="text-xs muted-text pb-1">
                  Consistent attendance and punctuality are essential to Hyvid's success. Frequent tardiness or unapproved absences may lead to disciplinary action.
                </p>

                <p className="text-xs font-semibold muted-text mt-2 mb-1">
                  No Call, No Show (NCNS)
                </p>

                <p className="text-xs muted-text mb-1">
                  An NCNS occurs when a team member fails to report for work and does not follow the required call-in procedure. Notify your immediate supervisor at least one (1) hour before your scheduled shift. You must notify your Manager each day you are absent, even for consecutive days.
                </p>

                <p className="text-xs font-semibold muted-text mb-1">
                  Return to Work Order (RTWO)
                </p>

                <p className="text-xs muted-text mb-2">
                  If an employee incurs three (3) consecutive NCNS occurrences, Hyvid will issue a Return to Work Order to the employee's last known address.
                </p>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Infraction</th>
                        <th className="px-3 py-2 text-left font-semibold">Corrective Action</th>
                        <th className="px-3 py-2 text-left font-semibold">Prescriptive Period</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">1st day</td>
                        <td className="px-3 py-2 align-top">First Written Warning</td>
                        <td className="px-3 py-2 align-top">6 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">2nd day</td>
                        <td className="px-3 py-2 align-top">Final Written Warning</td>
                        <td className="px-3 py-2 align-top">12 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">3rd day</td>
                        <td className="px-3 py-2 align-top">Dismissal</td>
                        <td className="px-3 py-2 align-top">Not applicable</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-xs font-semibold muted-text mt-3 mb-1">
                  Absences Due to Health Reasons (Failure to Comply)
                </p>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Infraction</th>
                        <th className="px-3 py-2 text-left font-semibold">Corrective Action</th>
                        <th className="px-3 py-2 text-left font-semibold">Prescriptive Period</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">1st offense</td>
                        <td className="px-3 py-2 align-top">First Written Warning</td>
                        <td className="px-3 py-2 align-top">6 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">2nd offense</td>
                        <td className="px-3 py-2 align-top">Final Written Warning</td>
                        <td className="px-3 py-2 align-top">12 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">3rd offense</td>
                        <td className="px-3 py-2 align-top">Dismissal</td>
                        <td className="px-3 py-2 align-top">Not applicable</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-xs font-semibold muted-text mt-3 mb-1">
                  Unauthorized Absences
                </p>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Infraction</th>
                        <th className="px-3 py-2 text-left font-semibold">Corrective Action</th>
                        <th className="px-3 py-2 text-left font-semibold">Prescriptive Period</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">1st Offense</td>
                        <td className="px-3 py-2 align-top">Verbal Warning</td>
                        <td className="px-3 py-2 align-top">3 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">2nd Offense</td>
                        <td className="px-3 py-2 align-top">1st Written Warning</td>
                        <td className="px-3 py-2 align-top">6 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">3rd Offense</td>
                        <td className="px-3 py-2 align-top">Final Written Warning</td>
                        <td className="px-3 py-2 align-top">12 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">4th Offense</td>
                        <td className="px-3 py-2 align-top">Dismissal</td>
                        <td className="px-3 py-2 align-top">Not applicable</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-xs font-semibold muted-text mt-3 mb-1">
                  Tardiness
                </p>

                <p className="text-xs muted-text mb-2">
                  Every three (3) instances of tardiness, or a total of three (3) hours of tardiness within one month, will be treated as an infraction.
                </p>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Infraction</th>
                        <th className="px-3 py-2 text-left font-semibold">Corrective Action</th>
                        <th className="px-3 py-2 text-left font-semibold">Prescriptive Period</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">1st Offense</td>
                        <td className="px-3 py-2 align-top">Verbal Warning</td>
                        <td className="px-3 py-2 align-top">3 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">2nd Offense</td>
                        <td className="px-3 py-2 align-top">1st Written Warning</td>
                        <td className="px-3 py-2 align-top">6 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">3rd Offense</td>
                        <td className="px-3 py-2 align-top">Final Written Warning</td>
                        <td className="px-3 py-2 align-top">12 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">4th Offense</td>
                        <td className="px-3 py-2 align-top">Dismissal</td>
                        <td className="px-3 py-2 align-top">Not applicable</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-xs font-semibold muted-text mt-3 mb-1">
                  Training Tardiness
                </p>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Infraction</th>
                        <th className="px-3 py-2 text-left font-semibold">Corrective Action</th>
                        <th className="px-3 py-2 text-left font-semibold">Prescriptive Period</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">1st Offense</td>
                        <td className="px-3 py-2 align-top">Verbal Warning</td>
                        <td className="px-3 py-2 align-top">3 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">2nd Offense</td>
                        <td className="px-3 py-2 align-top">1st Written Warning</td>
                        <td className="px-3 py-2 align-top">6 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">3rd Offense</td>
                        <td className="px-3 py-2 align-top">Final Written Warning</td>
                        <td className="px-3 py-2 align-top">12 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">4th Offense</td>
                        <td className="px-3 py-2 align-top">Dismissal</td>
                        <td className="px-3 py-2 align-top">Not applicable</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-xs font-semibold muted-text mt-3 mb-1">
                  Training Absence (NCNS / Unauthorised)
                </p>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Infraction</th>
                        <th className="px-3 py-2 text-left font-semibold">Corrective Action</th>
                        <th className="px-3 py-2 text-left font-semibold">Prescriptive Period</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">1st Offense</td>
                        <td className="px-3 py-2 align-top">Final Written Warning</td>
                        <td className="px-3 py-2 align-top">12 months</td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">2nd Offense</td>
                        <td className="px-3 py-2 align-top">Dismissal</td>
                        <td className="px-3 py-2 align-top">Not applicable</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-xs font-semibold muted-text mt-3 mb-1">
                  Other Attendance-Related Offenses
                </p>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Offense</th>
                        <th className="px-3 py-2 text-left font-semibold">Class</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          Wasting work time during official work hours by engaging in idle chatting, loitering, or loafing
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200">
                            Class A
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          Interfering with another employee on the job
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200">
                            Class A
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          Unjustified non-responsiveness in company communication platforms
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200">
                            Class B
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          Willfully restricting work output or encouraging others to do the same
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200">
                            Class B
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          Wasting work time for personal or third party's interest during official working hours
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200">
                            Class B
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          Leaving or interrupting assigned work during the work shift without permission from immediate supervisor
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200">
                            Class B
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          Unauthorized quitting work prior to the end of shift
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          Sleeping while on duty
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          Engaging in other employment or activity that conflicts with work schedules, duties, and responsibilities
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          Changing working hours, or days off without the immediate supervisor's permission
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          Feigning illness to avoid doing assigned work
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          Inciting or participating in concerted work stoppage, slow down, mass leave, sit down, riot, or other similar disruptive activities
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </details>

            <details className="group rounded-lg border border-gray-200 dark:border-gray-700 dark-mode-card overflow-hidden">
              <summary className="flex items-center justify-between px-3 py-2.5 cursor-pointer font-semibold muted-text text-xs hover:bg-gray-50 dark:hover:bg-gray-900/50">
                <span>Property and Assets</span>

                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
              </summary>

              <div className="px-3 pb-3 space-y-3">
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Offense</th>
                        <th className="px-3 py-2 text-left font-semibold">Class</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Negligence or improper use or handling of Hyvid property or facilities, equipment or materials resulting to damages/losses to Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Negligence causing the destruction, damage to, or loss of the property of a fellow employee, client, supplier or visitor</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Concealing or deliberately causing loss to Hyvid property</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Allowing unauthorized persons to use Hyvid supplies, facilities, or equipment</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Unauthorized use or possession of Hyvid property for personal or third party's interest</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Failure to give due notification or failure to provide the needed information to person concerned resulting in loss or damage to Hyvid property</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Failure to report defective equipment, furniture, and fixtures which might lead to accident/s or damage to life and property</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Committing other acts not embraced by other provisions which cause loss or damage to Hyvid property</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Malversation, misappropriation or unauthorized diversion or application of Hyvid funds</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Theft of Hyvid property or that of a co-employee</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Absconding with, or embezzlement of Hyvid funds</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Willful or deliberate or malicious destruction, defacement, vandalism or tampering with Hyvid property</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Selling or disposing of Hyvid property without authority</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Failure to return Hyvid property assigned to employee's custody when requested by supervisor or authorized Hyvid official</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Gross negligence resulting to grave damages/losses to the Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Committing other acts of deceit or anomaly not embraced by other provisions which cause loss or damage to Hyvid property</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </details>

            <details className="group rounded-lg border border-gray-200 dark:border-gray-700 dark-mode-card overflow-hidden">
              <summary className="flex items-center justify-between px-3 py-2.5 cursor-pointer font-semibold muted-text text-xs hover:bg-gray-50 dark:hover:bg-gray-900/50">
                <span>Dishonesty and Integrity</span>

                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
              </summary>

              <div className="px-3 pb-3 space-y-3">
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Offense</th>
                        <th className="px-3 py-2 text-left font-semibold">Class</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Fraud or willful breach by the employee of the trust reposed in him by Management or by its duly authorized representative</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Libel or making false or malicious statements concerning the Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Espionage or unauthorized obtaining of information from Hyvid or engaging others to spy or obtain information</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Submitting false or fraudulent money claims</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Submission of false reports, information or certification resulting in loss of revenue or damage to Hyvid image or affecting service obligation</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Providing incorrect information/testimonies in a Hyvid-authorized investigation or proceeding</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Falsification or irregularities in the accomplishment or keeping of time records</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Submission of false documents/false statements; or willful misrepresentation or concealment of disqualification to secure employment or promotion within Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Utilizing records or documents known by the user to be false</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Forging, falsifying, or altering official document/s in such a way as to mislead the user thereof</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Timing in or logging-in for another employee or conspiring with / arranging for another to do the same</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Assisting or helping to defraud Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Committing other acts of dishonesty not embraced by the foregoing provisions</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </details>

            <details className="group rounded-lg border border-gray-200 dark:border-gray-700 dark-mode-card overflow-hidden">
              <summary className="flex items-center justify-between px-3 py-2.5 cursor-pointer font-semibold muted-text text-xs hover:bg-gray-50 dark:hover:bg-gray-900/50">
                <span>Conduct and Behaviour</span>

                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
              </summary>

              <div className="px-3 pb-3 space-y-3">
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Offense</th>
                        <th className="px-3 py-2 text-left font-semibold">Class</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Serious misconduct or willful disobedience of the lawful instructions of his/her superior</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Soliciting funds or collecting contribution for any purpose, at any time on the premises, unless authorized by Management</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Obtaining or accepting anything of value by entering into an unauthorized arrangement with supplier, clients, or other persons</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Accepting, directly or indirectly, anything of value in exchange for a job, work assignment, work location or favorable conditions of employment</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Bribery or offering anything of value in exchange for a job, work assignment, work location or favorable conditions of employment</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Violation of the confidentiality agreements which include but are not limited to unauthorized access, disclosure, copying, or dissemination of confidential, restricted, private or personnel information</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Behaving with disrespect, discourtesy, or improper conduct towards clients, customers, and other persons transacting business with Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Damaging or jeopardizing Hyvid's interests through acts or omissions affecting clients, visitors, customers, and other persons transacting business with Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Negligence in the performance of duties causing damage to the image or interest of Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Acts of subversion including the distribution of subversive materials; and other acts inimical to the security and interest of Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Inciting or participating in concerted work stoppage, slow down, mass leave, sit down, riot, or other similar disruptive activities</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Engaging in activities which directly or indirectly compete with the business or the interests of Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Failure to notify the Management of close personal or intimate relationship between employees wherein one has influence on the hiring, termination, discipline, evaluation, promotion, compensation of the other</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Soliciting employment or business opportunities directly from the clients</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Failure to notify the Management of attempts made by clients or their agents, to offer employment or solicit services</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Gambling, betting, or participating in any form of 'game of chance' within Hyvid premises/facilities or on Hyvid time</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Refusal or failure to follow the official dress code of the Hyvid except during designated dress down or wash days</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200">
                            Class B
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Unjustified refusal to accept work responsibility, work shift/schedule and work location</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Unjustified failure to work overtime, or work on rest-day or holiday when the employee has signed up or has volunteered for the schedule prior, thus causing disruption in operations</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200">
                            Class B
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Gross or habitual neglect in the performance of assigned duties</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Selling of commodities and services, lending money with interest, peddling or conducting personal business that affect productivity within Hyvid premises, during scheduled work hours</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Committing acts of sexual harassment</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Committing lewd and lascivious acts during working hours or during Hyvid-sponsored events (e.g., excessive display of affection, voyeurism or indecent exposure)</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Behaving with disrespect, discourtesy, or improper conduct towards co-employees</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Acts of harassment or any offensive behavior usually for the purpose of threat or intimidation. This includes, but is not limited to, derogatory statements, remarks, jokes and slurs concerning race, ethnicity, age, religion, physical appearance, sexual orientation</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Using Hyvid's information and electronic communications systems to create the perception or reality of impropriety, or storing and/or transmitting data or any electronic media, in whatever form, which is offensive to employees, customers, and other users</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Bringing in or showing pornographic material</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Malicious gossip and/or spreading rumors; instigating and/or agitating intrigue among employees; engaging in behaviors designed to create discord and lack of harmony</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Commission of a crime or offense by the employee whether as a principal or accessory against the person of his employer or any immediate member of his family or his duly authorized representative</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Any attempt to inflict or cause bodily harm to another; or have in fact inflicted or caused bodily harm within the Hyvid premises</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Defamation or public accusation or any act that may cause dishonor or discredit an employee or a Hyvid official</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Taking the life of another employee either willfully or by negligence within the Hyvid premises, office or job site</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Blackmailing or threatening to reveal substantially true information about an employee in exchange for demand or favor</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Provoking or instigating a fight during working hours or within Hyvid or work related premises</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Committing other acts against Conduct and Behavior not embraced by the foregoing provisions</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class C/D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Committing other acts or omissions which are not embraced by the foregoing provisions which cause damage to the interests of Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </details>

            <details className="group rounded-lg border border-gray-200 dark:border-gray-700 dark-mode-card overflow-hidden">
              <summary className="flex items-center justify-between px-3 py-2.5 cursor-pointer font-semibold muted-text text-xs hover:bg-gray-50 dark:hover:bg-gray-900/50">
                <span>Health, Safety and Security</span>
                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
              </summary>

              <div className="px-3 pb-3 space-y-3">
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Offense</th>
                        <th className="px-3 py-2 text-left font-semibold">Class</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Use of prohibited drugs</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>
                            Bringing, possession, dealing and/or selling of any prohibited drug inside the Hyvid premises or during scheduled work hours
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Reporting for work intoxicated or under the influence of prohibited drugs</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Smoking in prohibited areas</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Reporting for work under the influence of alcohol</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Willful disregard of Hyvid directives relating to health, safety, security, and sanitation</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Failure to comply with health, safety and security requirements of Hyvid</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>
                            Lending Hyvid ID any identification material or password to any person not entitled to it. Assisting unauthorized persons to gain access to Hyvid systems, network and/or platforms
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Interfering in the conduct of security procedures</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Failure to report or non-disclosure of any communicable disease afflicting oneself or any employee</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Committing other acts against Health, Safety and Security not embraced by the foregoing provisions</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class C/D
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </details>
            <details className="group rounded-lg border border-gray-200 dark:border-gray-700 dark-mode-card overflow-hidden">
              <summary className="flex items-center justify-between px-3 py-2.5 cursor-pointer font-semibold muted-text text-xs hover:bg-gray-50 dark:hover:bg-gray-900/50">
                <span>Technology and Acceptable Use (IT)</span>
                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
              </summary>

              <div className="px-3 pb-3 space-y-3">
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                        <th className="px-3 py-2 text-left font-semibold">Offense</th>
                        <th className="px-3 py-2 text-left font-semibold">Class</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Unauthorized use of Hyvid IT resources (i.e. printing, plotting, photocopying for personal use)</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>
                            Sending trivial/personal messages (i.e. SPAM, e-mail group), unsolicited junk mail, "for profit" messages or chain letters; subscribing to non-business related e-mail groups and internet newsletters
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200">
                            Class B
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Unauthorized use of telephones, cellular phones or other communication facilities</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Unauthorized use of portable file storage devices</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Deliberate use of internet for purposes other than authorized business transactions</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200">
                            Class B
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Unauthorized installation or use of unlicensed computer software on Hyvid computers or network</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>
                            Importing, downloading, copying or forwarding non-text file, including e-mail attachments, applications, utilities, images, music and pages, from the internet and/or through unauthorized electronic media, onto one's system unless business related and/or IT authorized
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            Class C
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Tampering with set-up and/or configurations of computers</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Hacking or attempting to hack Hyvid's online assets and platforms</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Impersonating another person when using e-mail or amending messages received</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Sending e-mail messages / viewing websites containing sexual or illegal materials that are offensive in any way</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Accessing or attempting to access another user's work accounts without permission from the employee</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Unauthorized use of another employee's passwords or access credentials</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Playing computer games on Hyvid owned PCs</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 dark-mode-card">
                        <td className="px-3 py-2 align-top">
                          <span>Unauthorized copying, renaming, modification, deletion, or access of any system, data, directory, file, application program</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class D
                          </span>
                        </td>
                      </tr>

                      <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20">
                        <td className="px-3 py-2 align-top">
                          <span>Committing other acts against proper utilization of IT resources not embraced by the foregoing provisions</span>
                        </td>
                        <td className="px-3 py-2 align-top w-px">
                          <span className="inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200">
                            Class C/D
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
      <div className="mb-4">
        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
          Miscellaneous Provisions
        </h3>

        <div className="pl-4 text-sm muted-text leading-relaxed space-y-1.5">
          <div className="space-y-3">

            <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <strong>Equitable Treatment</strong> — Every situation is different, and Hyvid recognises that context matters. Each performance or behavioural infraction will be assessed based on its specific circumstances. Management reserves the right to apply a higher or lower penalty depending on aggravating or mitigating factors, such as intent, frequency, and overall conduct.
              </div>
            </div>

            <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <strong>Preventive Suspension</strong> — Hyvid may place an employee under preventive suspension if they are charged with a major or grave offense, and their continued presence could pose a serious or immediate risk to the safety of others or to company property. Preventive suspension: may last up to thirty (30) days; can be extended if the investigation requires more time (additional days will be paid); entry to company facilities is only permitted with written authorisation from People & Culture. Preventive suspension is not a penalty — it's a temporary measure to allow for a fair and safe investigation process.
              </div>
            </div>

            <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <strong>Two Final Written Warnings</strong> — Receiving two (2) Final Written Warnings within a 12-month period constitutes a grave offense and may result in termination of employment, subject to review and approval by the relevant Department Head and People & Culture.
              </div>
            </div>

            <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <strong>Civil and Criminal Charges</strong> — Disciplinary action by Hyvid does not prevent the company from taking legal action when necessary. If an offense results in damage, loss, or harm to Hyvid, its clients, or other parties, the company may pursue civil or criminal proceedings as appropriate. In addition to any internal penalties, the employee responsible may be required to repair or replace damaged property or reimburse financial losses incurred by Hyvid or its clients.
              </div>
            </div>

          </div>
        </div>
      </div>
      <div className="mt-8 p-5 bg-gradient-to-br from-emerald-50 to-purple-50 dark:from-emerald-900 dark:to-purple-900 rounded-xl border border-emerald-100 dark:border-emerald-800 text-center">
        <div className="text-base font-bold title-text mb-2">
          Final Note
        </div>

        <p className="text-sm muted-text max-w-lg mx-auto">
          This handbook reflects who we are — a global company built on{" "}
          <strong>trust, transparency, and teamwork</strong>. It's not just a
          rulebook — it's a shared understanding of how we create a workplace
          where everyone can thrive, wherever they are in the world.
        </p>

        <p className="mt-3 text-emerald-600 dark:text-emerald-500 font-semibold text-sm">
          Welcome to Hyvid - we're so glad you're here, and we can't wait to
          see what we'll achieve together.
        </p>

        <p className="mt-4 text-xs text-gray-400">
          © 2025 Hyvid Pty Ltd · hyvid.com · For internal use only
        </p>
      </div>
    </>
  ),
}
];

const EmployeeHandbookContents = ({ searchTerm }) => {
  const filteredSections = sections.filter((section) => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    
    return (
      section.sectionTitle.toLowerCase().includes(search) ||
      section.searchKeywords?.toLowerCase().includes(search)
    );
  });
  
  return (
    <ContentsContainer sections={filteredSections} />
  );
};

export default EmployeeHandbookContents;